/**
 * Removes an element from an array and mutates the original array.
 */
export function removeItem<T>(arr: Array<T>, value: T): Array<T> {
  const index = arr.indexOf(value);
  if (index > -1) {
    arr.splice(index, 1);
  }

  return arr;
}

/**
 * Removes an element from an array and returns the modified array. It does not mutate
 * the original array.
 */
export function withoutItem<T>(arr: Array<T>, value: T): Array<T> {
  const index = arr.indexOf(value);
  return [...arr.slice(0, index), ...arr.slice(index + 1, arr.length)];
}

/**
 * Removes duplicate elements from an array.
 */
export function getUnrepeatedElements<T>(arr: Array<T>): Array<T> {
  return arr.filter((x) => arr.indexOf(x) === arr.lastIndexOf(x));
}

/**
 * Splits or iterates over the array in groups of size `size`.
 */
export function chunks<T>(array: T[], size: number): T[][] {
  return [...Array(Math.ceil(array.length / size))].map((_, i) => array.slice(i * size, i * size + size));
}

/**
 * Wrap a value in an array if it is not already an array.
 */
export function wrap<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * Cycle forwards/backwards through a list of items
 *
 * @example
 * const elements = ['pen', 'pencil', 'chalk']
 *
 * cycle(elements, 'pencil', 1)
 * //=> 'chalk'
 *
 * cycle(elements, 'chalk', -1)
 * //=> 'pencil'
 *
 * cycle(elements, 'chalk', 1)
 * //=> 'pen'
 */
export function cycle<T>(items: T[], item: T, index: 1 | -1): T {
  let activeIndex = items.indexOf(item);

  if (activeIndex === items.length - 1 && index === 1) {
    activeIndex = -1;
  }

  let indexOfItem = index === 1 ? 0 : items.length - 1;
  if (item && activeIndex >= 0) {
    const newIndex = activeIndex + index;
    if (newIndex >= 0 && newIndex < items.length) {
      indexOfItem = newIndex;
    }
  }

  return items[indexOfItem];
}

/**
 * Toggles the presence of a value in a Set.
 *
 * **Note:** This mutates the original Set.
 *
 * @param set - The Set to modify.
 * @param value - The value to toggle.
 * @param force - Optional boolean to explicitly add (true) or delete (false) the value.
 *                If not provided, the value will be added if absent or removed if present.
 */
export function toggleSet<T>(set: Set<T>, value: T, force?: boolean) {
  if (typeof force !== 'boolean') {
    force = !set.has(value);
  }

  if (force) {
    set.add(value);
  } else {
    set.delete(value);
  }
}
